import React, { useState, useEffect } from 'react';

interface Category {
    name: string;
    count: number;
    label: string;
}

interface BlogCategoriesProps {
    selectedCategory: string;
    onCategoryChange: (category: string) => void;
}

export default function BlogCategories({ selectedCategory, onCategoryChange }: BlogCategoriesProps) {
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch('/api/blog/categories');
                const data = await response.json();

                if (data.success) {
                    setCategories(data.data);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    const getCategoryIcon = (category: string) => {
        const icons = {
            'grammar': '📚',
            'vocabulary': '🔤',
            'culture': '🌍',
            'tips': '💡',
            'success-stories': '🏆',
            'ai-learning': '🤖'
        };
        return icons[category as keyof typeof icons] || '📝';
    };

    const getCategoryColor = (category: string) => {
        const colors = {
            'grammar': 'hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200',
            'vocabulary': 'hover:bg-green-50 hover:text-green-600 hover:border-green-200',
            'culture': 'hover:bg-purple-50 hover:text-purple-600 hover:border-purple-200',
            'tips': 'hover:bg-yellow-50 hover:text-yellow-600 hover:border-yellow-200',
            'success-stories': 'hover:bg-pink-50 hover:text-pink-600 hover:border-pink-200',
            'ai-learning': 'hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200'
        };
        return colors[category as keyof typeof colors] || 'hover:bg-gray-50 hover:text-gray-600 hover:border-gray-200';
    };

    const getSelectedCategoryColor = (category: string) => {
        const colors = {
            'grammar': 'bg-blue-50 text-blue-600 border-blue-200',
            'vocabulary': 'bg-green-50 text-green-600 border-green-200',
            'culture': 'bg-purple-50 text-purple-600 border-purple-200',
            'tips': 'bg-yellow-50 text-yellow-600 border-yellow-200',
            'success-stories': 'bg-pink-50 text-pink-600 border-pink-200',
            'ai-learning': 'bg-indigo-50 text-indigo-600 border-indigo-200'
        };
        return colors[category as keyof typeof colors] || 'bg-gray-50 text-gray-600 border-gray-200';
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Categories</h3>
                <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">📂 Categories</h3>

            {/* All Categories */}
            <div className="space-y-2">
                <button
                    onClick={() => onCategoryChange('')}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all duration-200 text-left ${selectedCategory === ''
                            ? 'bg-gray-50 text-gray-700 border-gray-200'
                            : 'text-gray-600 border-gray-100 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-200'
                        }`}
                >
                    <div className="flex items-center space-x-3">
                        <span className="text-lg">📝</span>
                        <span className="font-medium">All Categories</span>
                    </div>
                    <span className="text-sm font-semibold px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                        {categories.reduce((total, cat) => total + cat.count, 0)}
                    </span>
                </button>

                {/* Individual Categories */}
                {categories.map((category) => (
                    <button
                        key={category.name}
                        onClick={() => onCategoryChange(category.name)}
                        className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all duration-200 text-left ${selectedCategory === category.name
                                ? getSelectedCategoryColor(category.name)
                                : `text-gray-600 border-gray-100 ${getCategoryColor(category.name)}`
                            }`}
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-lg">{getCategoryIcon(category.name)}</span>
                            <span className="font-medium">{category.label}</span>
                        </div>
                        <span className={`text-sm font-semibold px-2 py-1 rounded-full ${selectedCategory === category.name
                                ? 'bg-white/80 text-current'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                            {category.count}
                        </span>
                    </button>
                ))}
            </div>

            {/* Categories Description */}
            <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">What you'll find:</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                    <li>📚 <strong>Grammar:</strong> Rules, examples, and practice</li>
                    <li>🔤 <strong>Vocabulary:</strong> Word building and usage</li>
                    <li>🌍 <strong>Culture:</strong> Context and understanding</li>
                    <li>💡 <strong>Tips:</strong> Learning strategies and hacks</li>
                    <li>🏆 <strong>Success Stories:</strong> Real student journeys</li>
                    <li>🤖 <strong>AI Learning:</strong> Technology-enhanced methods</li>
                </ul>
            </div>
        </div>
    );
} 